import type { ReactNode } from 'react';
import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { BANNER_TITLE, InTitleSlot, useTitleInBanner } from './layout/page-title-slot';
import { TEXT } from './typography';

/**
 * The page title block of every screen that is not a drill-in detail (those use `DetailHeader`). The h1 (with its
 * back control) goes up into the app banner through `InTitleSlot`; the subtitle and the screen's own actions stay
 * in the page, as one compact row, subtitle left and actions right (the product owner's layout, 2026-09-25).
 * Ported from the HyperTek prototype's `components/layout/page-header.tsx`, so the title's size, the subtitle's
 * tone and the gap to the first section are the same on every screen.
 *
 * Deltas from the prototype, all forced by the product: the back control links with react-router (`to`, not
 * `href`), and when it is a link it wears the `button` hook so the touch floor and the responsive suite's
 * `a.button` gate reach it — the ghost `icon-sm` button already carries the 44 px floor.
 *
 * Back navigation: `backHref` (a route) or `onBack` (a callback) renders an icon-only back button inline,
 * before the title. `backLabel` is its accessible name and tooltip.
 */
export function PageHeader({
  title, titleAside, subtitle, actions, backHref, onBack, backLabel = 'Quay lại', className,
}: {
  title: ReactNode;
  /** Controls that sit beside the title in the banner. */
  titleAside?: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  /** Route to navigate back to — renders an icon-only back button before the title. */
  backHref?: string;
  /** Back callback — alternative to `backHref`; takes precedence when both are set. */
  onBack?: () => void;
  /** Accessible name for the back button (aria-label + title). */
  backLabel?: string;
  className?: string;
}) {
  const inBanner = useTitleInBanner();
  const backIcon = <ArrowLeft aria-hidden="true" className="size-4" />;
  const backClass = '-ml-1 shrink-0 text-[hsl(var(--h-muted-foreground))]';
  const back = onBack
    ? (
      <Button
        type="button" variant="ghost" size="icon-sm" onClick={onBack}
        aria-label={backLabel} title={backLabel} className={backClass}
      >
        {backIcon}
      </Button>
    )
    : backHref
      ? (
        <Button variant="ghost" size="icon-sm" asChild aria-label={backLabel} title={backLabel} className={cn('button', backClass)}>
          <Link to={backHref}>{backIcon}</Link>
        </Button>
      )
      : null;

  const heading = (
    <div className="flex min-w-0 items-center gap-2">
      {back}
      <h1 className={cn(TEXT.pageTitle, inBanner ? BANNER_TITLE : 'wrap-anywhere')} title={inBanner && typeof title === 'string' ? title : undefined}>
        {title}
      </h1>
      {titleAside}
    </div>
  );
  const row = subtitle || actions
    ? (
      <div className={cn('flex flex-wrap items-center justify-between gap-x-3 gap-y-2', className)}>
        {subtitle ? <p className={cn('min-w-0', TEXT.pageSubtitle)}>{subtitle}</p> : null}
        {/* `max-w-full`: never wider than the row, so the actions wrap inside it rather than run past the edge,
            where `html`'s `overflow-x: hidden` would cut them off unseen (the dashboard at 360 px). */}
        {actions ? <div className="ml-auto flex max-w-full shrink-0 flex-wrap items-center gap-2">{actions}</div> : null}
      </div>
    )
    : null;

  return (
    <>
      <InTitleSlot>{heading}</InTitleSlot>
      {row}
    </>
  );
}

/**
 * The outer wrapper of a screen: the compact `space-y-4` rhythm (the prototype's `space-y-6`, a third tighter) between the header and the sections. The
 * inset comes from `<main>`, so it carries no padding of its own.
 */
export function PageContainer({ className, children }: { className?: string; children: ReactNode }) {
  return <div className={cn('space-y-4', className)}>{children}</div>;
}
